import React, { useEffect, useMemo, useRef, useState } from "react";
import { BRL0, DIAS_SEMANA_JS, pad2, dataLocal } from "../formato";
import { centrarRolagem, curva, passoEixo, projecaoDiaria, restanteDeHoje } from "../calculos";

const W = 1240, H = 320, L = 84, R = 30, T = 30, B = 52;
const SIGLA = ["DOM", "SEG", "TER", "QUA", "QUI", "SEX", "SÁB"];
const HALO = { paintOrder: "stroke", stroke: "var(--vg-surface)", strokeWidth: 4, strokeLinejoin: "round" };

/** FATURAMENTO POR DIA do mês: linha, média dos dias fechados e projeção. */
export default function FaturamentoPorDia({ mes }) {
  const base = mes.dias[0].data.slice(0, 8); // "AAAA-MM-"
  const diaJS = (d) => dataLocal(base + pad2(d)).getDay();
  const valores = useMemo(() => mes.dias.map((d) => d.faturamento), [mes]);
  const diasMes = mes.dias_no_mes, diaHoje = valores.length;
  const { media, proj } = useMemo(() => projecaoDiaria({
    valores, diasNoMes: diasMes, esperadoRestanteHoje: restanteDeHoje(mes, "faturamento"), diaSemana: diaJS,
  }), [valores, diasMes, mes]); // eslint-disable-line react-hooks/exhaustive-deps
  const [sel, setSel] = useState(null);
  const svgRef = useRef(null);
  const rolagem = useRef(null);
  useEffect(() => { centrarRolagem(rolagem.current, (L + (diaHoje - 1) * ((W - L - R) / (diasMes - 1))) / W); }, [diaHoje, diasMes]);

  const todos = [...valores, ...Object.values(proj)];
  const passo = passoEixo(Math.max(...todos, 1) * 1.15);
  const max = Math.ceil((Math.max(...todos, 1) * 1.15) / passo) * passo;
  const step = (W - L - R) / (diasMes - 1);
  const x = (d) => L + (d - 1) * step;
  const y = (v) => T + (H - T - B) * (1 - v / max);
  const pts = valores.map((v, i) => [x(i + 1), y(v)]);

  // Faixas alternadas por semana (domingo a sábado).
  const faixas = [];
  for (let d = 1, sem = 0, ini = 1; d <= diasMes + 1; d++) {
    if ((d > diasMes || diaJS(d) === 0) && d > ini) {
      if (sem % 2 === 1) faixas.push([ini, d - 1]);
      sem++;
      ini = d;
    }
  }
  const grade = [];
  for (let v = 0; v <= max + 0.01; v += passo) grade.push(v);
  const linhaProj = diaHoje > 1 ? [[x(diaHoje - 1), y(valores[diaHoje - 2])]] : [];
  for (let d = diaHoje; d <= diasMes; d++) linhaProj.push([x(d), y(proj[d])]);

  return (
    <div ref={rolagem} className="vg-chart scroll">
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Faturamento por dia" onMouseLeave={() => setSel(null)}>
        <defs>
          <linearGradient id="vg-gp" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor="var(--vg-brand)" stopOpacity=".18" />
            <stop offset="1" stopColor="var(--vg-brand)" stopOpacity="0" />
          </linearGradient>
        </defs>
        {faixas.map(([a, b]) => (
          <rect key={a} x={x(a) - step / 2} y={T} width={x(b) - x(a) + step} height={y(0) - T} rx="4" fill="var(--vg-brand)" opacity=".05" />
        ))}
        {grade.map((v) => (
          <g key={v}>
            <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke="var(--vg-line-soft)" />
            <text x={L - 12} y={y(v) + 4} textAnchor="end" fontSize="11.5" fontWeight="500" fill="var(--vg-muted)">{v === 0 ? "R$ 0" : BRL0(v)}</text>
          </g>
        ))}
        <path d={`${curva(pts)} L${x(diaHoje)} ${y(0)} L${x(1)} ${y(0)} Z`} fill="url(#vg-gp)" />
        {media != null && (
          <g>
            <line x1={L} x2={W - R} y1={y(media)} y2={y(media)} stroke="var(--vg-good)" strokeWidth="1.5" strokeDasharray="2 6" />
            <text x={L + 6} y={y(media) - 7} fontSize="11.5" fontWeight="600" fill="var(--vg-good)">média {BRL0(media)}</text>
          </g>
        )}
        <path d={curva(linhaProj)} fill="none" stroke="var(--vg-brand)" strokeWidth="2.5" strokeDasharray="3 7" strokeLinecap="round" opacity=".9" />
        {Object.keys(proj).map(Number).map((d) => (
          <circle key={`p${d}`} cx={x(d)} cy={y(proj[d])} r={d === diaHoje ? 5 : 3.5} fill="var(--vg-surface)" stroke="var(--vg-brand)" strokeWidth="2.5" />
        ))}
        <path d={curva(pts)} fill="none" stroke="var(--vg-brand)" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
        {valores.map((v, i) => (
          <circle key={`v${i}`} cx={x(i + 1)} cy={y(v)} r={i + 1 === diaHoje ? 7 : 4.5} fill="var(--vg-brand)" stroke="var(--vg-surface)" strokeWidth="2.5" />
        ))}
        <text x={x(diaHoje)} y={y(proj[diaHoje]) - 14} textAnchor="middle" fontSize="12.5" fontWeight="600" fill="var(--vg-brand)" style={HALO}>
          {BRL0(proj[diaHoje])}
        </text>
        {Array.from({ length: diasMes }, (_, k) => k + 1).map((d) => {
          const hoje = d === diaHoje, fut = d > diaHoje;
          return (
            <g key={`x${d}`}>
              {hoje && <rect x={x(d) - 14} y={H - 38} width="28" height="34" rx="8" fill="var(--vg-brand-soft)" />}
              <text x={x(d)} y={H - 23} textAnchor="middle" fontSize="12" fontWeight={hoje ? 700 : 600}
                fill={hoje ? "var(--vg-brand)" : fut ? "var(--vg-muted-2)" : "var(--vg-text-2)"}>{pad2(d)}</text>
              <text x={x(d)} y={H - 9} textAnchor="middle" fontSize="9" fontWeight="600" letterSpacing=".04em"
                fill={hoje ? "var(--vg-brand)" : "var(--vg-muted-2)"}>{SIGLA[diaJS(d)]}</text>
            </g>
          );
        })}
        {sel != null && <line x1={x(sel)} x2={x(sel)} y1={T} y2={y(0)} stroke="var(--vg-muted)" strokeDasharray="3 4" opacity=".6" />}
        {Array.from({ length: diasMes }, (_, k) => k + 1).map((d) => (
          <rect key={`h${d}`} x={x(d) - step / 2} y={T} width={step} height={y(0) - T} fill="transparent" onMouseEnter={() => setSel(d)} />
        ))}
      </svg>
      {sel != null && (() => {
        const k = svgRef.current ? svgRef.current.getBoundingClientRect().width / W : 1;
        const largura = svgRef.current?.getBoundingClientRect().width || W;
        const v = valores[sel - 1];
        const dm = media ? Math.round((v / media - 1) * 100) : null;
        return (
          <div className="vg-tip" style={{ left: Math.max(100, Math.min(largura - 100, x(sel) * k)), top: T * k - 8 }}>
            <h4>{DIAS_SEMANA_JS[diaJS(sel)]}, {pad2(sel)}/{base.slice(5, 7)} <small>{sel < diaHoje ? base.slice(0, 4) : sel === diaHoje ? "hoje" : "projeção"}</small></h4>
            {sel <= diaHoje ? (
              <>
                <div><span><i style={{ background: "var(--vg-brand)" }} />Vendido{sel === diaHoje ? " (até agora)" : ""}</span><b>{BRL0(v)}</b></div>
                {dm != null && <div><span>Vs. média do mês</span><b className={dm >= 0 ? "good" : "bad"}>{dm >= 0 ? "+" : ""}{dm}%</b></div>}
                {sel === diaHoje && <div className="proj"><span>Projeção de fechamento</span><b>{BRL0(proj[sel])}</b></div>}
                {media != null && <div><span><i style={{ background: "var(--vg-good)" }} />Média</span><b>{BRL0(media)}</b></div>}
              </>
            ) : (
              <div className="proj"><span><i style={{ background: "var(--vg-brand)" }} />Projeção</span><b>{BRL0(proj[sel])}</b></div>
            )}
          </div>
        );
      })()}
      <div className="vg-legend"><span><i />Faturamento</span><span><i className="avg" />Média</span><span><i className="proj" />Projeção</span></div>
    </div>
  );
}
